<script lang="ts">
export default {
  name: 'DocumentStatusTable',
};
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';
import { CloudUpload, FileText, Eye, EyeOff, Send, RefreshCw } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { useToast } from '@/composables/useToast';
import { documentService } from '@/services/v2/DocumentService';
import type { DocumentType, PolicyDocumentStatusRow } from '@/services/v2/ClientReadinessService';
import { useDataReadinessWizard } from '@/composables/useDataReadinessWizard';

const props = defineProps<{
  documentType: DocumentType;
}>();

const wizard = useDataReadinessWizard();
const toast = useToast();

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');

// Stable display slots keyed by policyId. A policy is assigned a slot the first
// time we see it and never moves, so uploading a doc — which flips a row from
// "missing" to "unpublished" — doesn't make the row jump to a different group.
// Wizard filters are currently fixed (current/CAC-only), so the policy set
// can't swap mid-session and the frozen order never resets.
const orderByPolicyId = new Map<number, number>();
let nextOrder = 0;

const rows = computed(() => {
  const source = props.documentType === 'Binder' ? wizard.binderRows.value : wizard.policyRows.value;
  const unseen = source.filter((r) => !orderByPolicyId.has(r.policyId));
  if (unseen.length) {
    // Assign slots in the order the row would first appear: missing first, then
    // alphabetical by description.
    unseen
      .slice()
      .sort((a, b) => {
        const aMissing = a.status === 'missing' ? 0 : 1;
        const bMissing = b.status === 'missing' ? 0 : 1;
        if (aMissing !== bMissing) return aMissing - bMissing;
        return (a.description ?? '').localeCompare(b.description ?? '', undefined, { sensitivity: 'base' });
      })
      .forEach((r) => orderByPolicyId.set(r.policyId, nextOrder++));
  }
  return [...source].sort((a, b) => orderByPolicyId.get(a.policyId)! - orderByPolicyId.get(b.policyId)!);
});

// Initial load only — a refetch with rows already on screen keeps them visible.
const isLoading = computed(
  () => (props.documentType === 'Binder' ? wizard.loadingBinders.value : wizard.loadingPolicies.value) && rows.value.length === 0,
);

const totalCount = computed(() => rows.value.length);
const missingCount = computed(() => rows.value.filter((r) => r.status === 'missing').length);
const unpublishedCount = computed(() => rows.value.filter((r) => r.status === 'unpublished').length);
const publishedCount = computed(() => rows.value.filter((r) => r.status === 'published').length);

const labels = computed(() => {
  if (props.documentType === 'Binder') {
    return {
      fileHeader: 'Binder file',
      missingChip: 'missing binder',
      uploadCta: 'Upload binder',
      drawerHeading: (policyNumber: string) => `Upload binder for ${policyNumber}`,
      hintSingular: 'binder',
      hintPlural: 'binders',
    };
  }
  return {
    fileHeader: 'Policy file',
    missingChip: 'missing document',
    uploadCta: 'Upload document',
    drawerHeading: (policyNumber: string) => `Upload policy document for ${policyNumber}`,
    hintSingular: 'document',
    hintPlural: 'documents',
  };
});

const footerHint = computed<{ text: string; color: 'red' | 'amber' | 'green' }>(() => {
  if (missingCount.value > 0) {
    const n = missingCount.value;
    return {
      text: `${n} ${n === 1 ? labels.value.hintSingular : labels.value.hintPlural} still missing`,
      color: 'red',
    };
  }
  if (publishedCount.value === 0) {
    return {
      text: `Publish at least one ${labels.value.hintSingular} to continue`,
      color: 'amber',
    };
  }
  const n = publishedCount.value;
  return {
    text: `${n} ${n === 1 ? labels.value.hintSingular : labels.value.hintPlural} published — ready to proceed`,
    color: 'green',
  };
});

watchEffect(() => {
  if (isLoading.value) {
    wizard.setFooterHint({ text: 'Checking documents…', color: 'muted' });
  } else {
    wizard.setFooterHint(footerHint.value);
  }
});
onBeforeUnmount(() => wizard.setFooterHint(null));

const openDrawerPolicyId = ref<number | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const pickerTargetRow = ref<PolicyDocumentStatusRow | null>(null);
const uploadingPolicyId = ref<number | null>(null);
const togglingDocumentId = ref<string | null>(null);
const draggingOver = ref(false);
const recentlyUploadedPolicyId = ref<number | null>(null);

function toggleDrawer(policyId: number) {
  openDrawerPolicyId.value = openDrawerPolicyId.value === policyId ? null : policyId;
  draggingOver.value = false;
}

function closeDrawer() {
  openDrawerPolicyId.value = null;
  draggingOver.value = false;
}

function triggerFilePicker(row: PolicyDocumentStatusRow) {
  pickerTargetRow.value = row;
  fileInputRef.value?.click();
}

async function uploadFileForRow(file: File, row: PolicyDocumentStatusRow) {
  const lowerName = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
    toast.error('Only PDF or DOCX files are allowed.');
    return;
  }

  uploadingPolicyId.value = row.policyId;
  try {
    await documentService.uploadDocumentForPolicyOrAccount({
      accountId: wizard.accountId.value,
      policyId: row.policyId,
      domain: 'Policy',
      type: props.documentType,
      fileName: file.name,
      document: file,
    });
    await wizard.refreshDocuments(props.documentType);
    closeDrawer();
    toast.success('File uploaded — publish to make it visible to the client.');
    flashRowSuccess(row.policyId);
  } catch {
    toast.error('Upload failed. Please try again.');
  } finally {
    uploadingPolicyId.value = null;
  }
}

function flashRowSuccess(policyId: number) {
  recentlyUploadedPolicyId.value = policyId;
  window.setTimeout(() => {
    if (recentlyUploadedPolicyId.value === policyId) {
      recentlyUploadedPolicyId.value = null;
    }
  }, 300);
}

async function handleFileChosen(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  const row = pickerTargetRow.value;
  pickerTargetRow.value = null;
  if (!file || !row) return;
  await uploadFileForRow(file, row);
}

function handleDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;
}

async function handleDrop(e: DragEvent, row: PolicyDocumentStatusRow) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  let file: File | null = null;
  if (e.dataTransfer?.items) {
    for (const item of e.dataTransfer.items) {
      if (item.kind === 'file') {
        file = item.getAsFile();
        if (file) break;
      }
    }
  }
  if (!file && e.dataTransfer?.files?.length) {
    file = e.dataTransfer.files[0];
  }
  if (!file) return;
  await uploadFileForRow(file, row);
}

async function publish(row: PolicyDocumentStatusRow) {
  if (!row.documentId) return;
  const docId = row.documentId;
  togglingDocumentId.value = docId;
  const snapshot = { status: row.status, published: row.published };
  row.status = 'published';
  row.published = true;
  try {
    await documentService.publishDocument(docId);
    toast.success(`${row.mainPolicyNumber} published to client portal.`);
  } catch {
    row.status = snapshot.status;
    row.published = snapshot.published;
    toast.error('Publish failed. Please try again.');
  } finally {
    togglingDocumentId.value = null;
  }
}

async function unpublish(row: PolicyDocumentStatusRow) {
  if (!row.documentId) return;
  const docId = row.documentId;
  togglingDocumentId.value = docId;
  const snapshot = { status: row.status, published: row.published };
  row.status = 'unpublished';
  row.published = false;
  try {
    await documentService.unPublishDocument(docId);
    toast.success(`${row.mainPolicyNumber} unpublished.`);
  } catch {
    row.status = snapshot.status;
    row.published = snapshot.published;
    toast.error('Unpublish failed. Please try again.');
  } finally {
    togglingDocumentId.value = null;
  }
}
</script>

<template>
  <div>
    <!-- Summary chips -->
    <div class="mb-4 flex flex-wrap gap-2">
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span class="font-semibold">{{ totalCount }}</span>
        <span class="text-base-content/70">total policies</span>
      </div>
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span
          class="font-semibold"
          :class="missingCount > 0 ? 'text-error' : ''"
          >{{ missingCount }}</span
        >
        <span class="text-base-content/70">{{ labels.missingChip }}</span>
      </div>
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span
          class="font-semibold"
          :class="unpublishedCount > 0 ? 'text-warning' : ''"
          >{{ unpublishedCount }}</span
        >
        <span class="text-base-content/70">uploaded, not published</span>
      </div>
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span
          class="font-semibold"
          :class="publishedCount > 0 ? 'text-success' : ''"
          >{{ publishedCount }}</span
        >
        <span class="text-base-content/70">published</span>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto rounded-md border border-base-300">
      <table class="table table-sm">
        <thead>
          <tr class="bg-base-200 text-xs tracking-wide text-base-content/60 uppercase">
            <th>Policy number</th>
            <th>Description</th>
            <th data-testid="document-status-file-header">{{ labels.fileHeader }}</th>
            <th>Status</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="isLoading">
            <tr
              v-for="i in 4"
              :key="`skeleton-${i}`">
              <td><Skeleton size="sm" /></td>
              <td><Skeleton /></td>
              <td><Skeleton /></td>
              <td><Skeleton width="6rem" height="1.25rem" /></td>
              <td><Skeleton width="5rem" class="ml-auto" /></td>
            </tr>
          </template>
          <template
            v-for="row in rows"
            :key="row.policyId">
            <tr
              class="transition-colors duration-1500"
              :class="{
                'bg-primary/5': openDrawerPolicyId === row.policyId,
                'bg-success/30 duration-[0ms]': recentlyUploadedPolicyId === row.policyId,
              }">
              <td class="font-mono text-xs">{{ row.mainPolicyNumber }}</td>
              <td>{{ row.description }}</td>
              <td>
                <span
                  v-if="row.documentName"
                  class="inline-flex items-center gap-1 font-mono text-xs text-primary">
                  <FileText :size="14" />
                  {{ row.documentName }}
                </span>
                <span
                  v-else
                  class="text-xs text-base-content/40 italic"
                  >No file uploaded</span
                >
              </td>
              <td>
                <span
                  v-if="row.status === 'published'"
                  class="badge gap-1 badge-soft badge-sm badge-success whitespace-nowrap">
                  <Eye :size="11" />
                  Published
                </span>
                <span
                  v-else-if="row.status === 'unpublished'"
                  class="badge gap-1 badge-ghost badge-sm whitespace-nowrap">
                  <EyeOff :size="11" />
                  Not published
                </span>
                <span
                  v-else
                  class="badge gap-1 badge-soft badge-sm badge-error whitespace-nowrap">
                  Missing
                </span>
              </td>
              <td class="text-right">
                <div class="inline-flex flex-wrap justify-end gap-1.5">
                  <AppButton
                    v-if="row.status === 'missing'"
                    primary
                    size="xs"
                    @click="toggleDrawer(row.policyId)">
                    <CloudUpload :size="14" />
                    {{ labels.uploadCta }}
                  </AppButton>
                  <AppButton
                    v-if="row.status === 'unpublished'"
                    success
                    size="xs"
                    :disabled="togglingDocumentId === row.documentId"
                    @click="publish(row)">
                    <Send :size="14" />
                    Publish to client
                  </AppButton>
                  <AppButton
                    v-if="row.status === 'published'"
                    ghost
                    size="xs"
                    :disabled="togglingDocumentId === row.documentId"
                    @click="unpublish(row)">
                    <EyeOff :size="14" />
                    Unpublish
                  </AppButton>
                  <AppButton
                    v-if="row.status !== 'missing'"
                    outline
                    size="xs"
                    @click="toggleDrawer(row.policyId)">
                    <RefreshCw :size="14" />
                    Replace file
                  </AppButton>
                </div>
              </td>
            </tr>
            <tr v-if="openDrawerPolicyId === row.policyId">
              <td
                colspan="5"
                class="bg-base-200">
                <div class="rounded-md border border-base-300 bg-base-100 p-4">
                  <h4 class="mb-3 text-sm font-semibold">{{ labels.drawerHeading(row.mainPolicyNumber) }}</h4>
                  <div
                    class="mb-3 flex items-center gap-3 rounded-md border-2 border-dashed px-4 py-3 transition-colors"
                    :class="draggingOver ? 'border-primary bg-primary/10' : 'border-base-300 bg-base-200/50'"
                    @dragover="handleDragOver"
                    @dragleave="handleDragLeave"
                    @drop="handleDrop($event, row)">
                    <CloudUpload
                      :size="22"
                      class="text-base-content/40" />
                    <div class="flex-1">
                      <div class="text-sm font-semibold">Drag & drop or browse</div>
                      <div class="text-xs text-base-content/60">PDF or DOCX</div>
                    </div>
                    <AppButton
                      primary
                      size="xs"
                      :loading="uploadingPolicyId === row.policyId"
                      :disabled="uploadingPolicyId === row.policyId"
                      @click="triggerFilePicker(row)">
                      <CloudUpload
                        v-if="uploadingPolicyId !== row.policyId"
                        :size="14" />
                      Browse files
                    </AppButton>
                  </div>
                  <AppButton
                    ghost
                    size="xs"
                    @click="closeDrawer">
                    Cancel
                  </AppButton>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="rows.length === 0 && !isLoading">
            <td
              colspan="5"
              class="py-8 text-center text-sm text-base-content/60">
              No policies found for this client.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <input
      ref="fileInputRef"
      type="file"
      :accept="ACCEPT_ATTR"
      class="hidden"
      @change="handleFileChosen" />
  </div>
</template>
